import { router, usePage } from "@inertiajs/react";
import { useState, useEffect } from "react";

import { useToast } from "/utils/toast-context.jsx";

import Modal from "/components/Modal";
import Button from "/components/Button";
import Select from "/components/Select";
import DateField from "/components/DateField";
import TextField from "/components/TextField";
import MultiSelect from "/components/MultiSelect";
import DropZone from "/components/DropZone";
import TextArea from "/components/TextArea";

import LayoutForm from "./LayoutForm";
import TeamForm from "./TeamForm";

export default function ProjectForm({
  onSubmit,
  useStore,
  errors,
}) {
  const { errorToast } = useToast();

  useEffect(() => {
    errors && errors.base && errors.base.forEach((error) => {
      errorToast(error);
    });
  }, [errors]);

  const store = useStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteProject = async () => {
    await router.delete(`/admin/projects/${store.id}`);
    setIsDeleteModalOpen(false);
  };

  const {
    project_visibilities,
    client_options,
    industry_options,
    project_type_options,
  } = usePage().props;

  return (
    <>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="modal--autowidth"
      >
        <div className="modal__text">
          Вы уверены, что хотите<br />удалить этот проект?
        </div>

        <div className="modal__actions">
          <Button onClick={deleteProject} className="btn btn--dark">Удалить</Button>
          <Button onClick={() => setIsDeleteModalOpen(false)}>Отменить</Button>
        </div>
      </Modal>

      <div className="frm-wrapper">
        <form className="frm">
          <ActionsBar store={store} onSubmit={onSubmit} setIsDeleteModalOpen={setIsDeleteModalOpen} />

          <div className="inpt-line">
            <DateField
              label="Дата создания проекта"
              value={store.date}
              onChange={store.setDate}
            />

            <TextField
              label="Порядок"
              placeholder="Введите порядковый номер"
              value={store.order}
              onChange={store.setOrder}
              errors={errors?.order}
            />
          </div>

          <div className="inpt-line">
            <Select
              label="Видимость проекта"
              options={project_visibilities}
              value={store.visibility}
              onChange={store.setVisibility}
            />
          </div>

          <div className="inpt-line">
            <TextField
              label="Слаг (человеко-понятный URL)"
              placeholder="Введите слаг"
              value={store.slug}
              onChange={store.setSlug}
              errors={errors?.slug}
            />
          </div>

          <div className="inpt-line">
            <TextField
              label="Название проекта"
              placeholder="Введите название проекта"
              value={store.title_ru}
              onChange={store.setTitleRu}
            />

            <TextField
              label="Project name"
              placeholder="Please specify the project name"
              value={store.title_en}
              onChange={store.setTitleEn}
            />
          </div>

          <div className="inpt-line">
            <Select
              label="Клиент"
              options={client_options}
              locale="ru"
              placeholder="Выберите из списка"
              value={store.client_id}
              onChange={store.setClientId}
              errors={errors?.client}
            />

            <Select
              label="Client"
              options={client_options}
              locale="en"
              placeholder="Select from the Russian dropdown list"
              value={store.client_id}
              disabled
            />
          </div>

          <div className="inpt-line">
            <MultiSelect
              label="Тип проекта"
              options={project_type_options}
              locale="ru"
              placeholder="Выберите из списка"
              value={store.project_type_ids}
              onChange={store.setProjectTypeIds}
            />

            <MultiSelect
              label="Project type"
              options={project_type_options}
              locale="en"
              placeholder="Select from the Russian dropdown list"
              value={store.project_type_ids}
              disabled
            />
          </div>

          <div className="inpt-line">
            <Select
              label="Индустрия"
              options={industry_options}
              locale="ru"
              placeholder="Выберите из списка"
              value={store.industry_id}
              onChange={store.setIndustryId}
            />

            <Select
              label="Industry"
              options={industry_options}
              value={store.industry_id}
              locale="en"
              placeholder="Select from the Russian dropdown list"
              disabled
            />
          </div>

          <div className="inpt-line">
            <div className="frm frm--small">
              <DropZone
                label="Привью для шеринга 1200x630"
                className="project-preview-dropzone"
                value={store.og_image_ru}
                onChange={store.setOgImageRu}
              />

              <TextField
                label="Заголовок для шеринга"
                placeholder="Студия Пууул"
                value={store.og_title_ru}
                onChange={store.setOgTitleRu}
              />

              <TextArea
                label="Текст для шеринга"
                placeholder="Введите краткий текст для шеринга, чтобы оно занимало 2-3 предложения"
                value={store.og_description_ru}
                onChange={store.setOgDescriptionRu}
              />
            </div>

            <div className="frm frm--small">
              <DropZone
                label="Preview for sharing 1200x630"
                className="project-preview-dropzone"
                value={store.og_image_en}
                onChange={store.setOgImageEn}
              />

              <TextField
                label="Title for sharing"
                placeholder="Poool Studio"
                value={store.og_title_en}
                onChange={store.setOgTitleEn}
              />

              <TextArea
                label="Description for sharing"
                placeholder="Please provide a brief project description that is 2-3 sentences long"
                value={store.og_description_en}
                onChange={store.setOgDescriptionEn}
              />
            </div>
          </div>

          {store.layouts.filter(layout => !layout.getState()._destroy).length > 0 && (
            <div className="flex-col gap-10">
              {store.layouts.toSorted((a, b) => a.getState().order - b.getState().order).map((useLayoutStore) => {
                const { key, _destroy } = useLayoutStore.getState();
                const onRemove = () => store.removeLayout(key);
                const onOrderUp = () => store.orderUpLayout(key);
                const onOrderDown = () => store.orderDownLayout(key);

                return !_destroy && (
                  <LayoutForm
                    key={key}
                    useStore={useLayoutStore}
                    onRemove={onRemove}
                    onOrderUp={onOrderUp}
                    onOrderDown={onOrderDown}
                  />
                );
              })}
            </div>
          )}

          {store.teams.filter(team => !team.getState()._destroy).length > 0 && (
            <div className="flex-col gap-10">
              {store.teams.toSorted((a, b) => a.getState().order - b.getState().order).map((useTeamStore) => {
                const { key, _destroy } = useTeamStore.getState();
                const onRemove = () => store.removeTeam(key);
                const onOrderUp = () => store.orderUpTeam(key);
                const onOrderDown = () => store.orderDownTeam(key);

                return !_destroy && (
                  <TeamForm
                    key={key}
                    useStore={useTeamStore}
                    onRemove={onRemove}
                    onOrderUp={onOrderUp}
                    onOrderDown={onOrderDown}
                  />
                );
              })}
            </div>
          )}

          <div className="flex-row gap-10px mt-10">
            <Button onClick={() => store.addLayout("image")}>Добавить картинку</Button>
            <Button onClick={() => store.addLayout("video")}>Добавить видео</Button>
            <Button onClick={() => store.addLayout("lottie")}>Добавить лотти</Button>
            <Button onClick={store.addTeam}>Добавить команду</Button>
            <button className="btn" disabled>Вставить код</button>
          </div>

          <ActionsBar store={store} onSubmit={onSubmit} setIsDeleteModalOpen={setIsDeleteModalOpen} />
        </form>
      </div>
    </>
  );
}

function ActionsBar({
  store,
  onSubmit,
  setIsDeleteModalOpen,
}) {
  return (
    <div className="actions-bar">
      <div>
        <Button className="lnk lnk--green" onClick={() => onSubmit('change')}>Сохранить</Button>
        <Button className="lnk lnk--blue"  onClick={() => onSubmit('publish')}>Сохранить и опубликовать</Button>
        <Button className="lnk" onClick={() => onSubmit('change', true)}>Превью</Button>
      </div>

      <div>
        <a href="/admin/projects" className="lnk">Отмена</a>
        <Button className="lnk" onClick={() => onSubmit('draft')}>В черновики</Button>
        {store.id && <Button onClick={() => setIsDeleteModalOpen(true)} className="lnk lnk--red">Удалить</Button>}
      </div>
    </div>
  );
}
